/**
 * GeoContext — Provides visitor's country, currency, and exchange rates
 * Data is fetched from CF Pages Functions and cached in localStorage for 24h.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { TCurrency, getCurrencyForCountry } from 'types/marketplace'

const GEO_CACHE_KEY = 'wonderlyar_geo_cache'
const RATES_CACHE_KEY = 'wonderlyar_rates_cache'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

export interface IGeoContext {
    visitorCountry: string | null
    visitorCurrency: TCurrency
    exchangeRates: Record<string, number>
    isLoading: boolean
}

const defaultGeoContext: IGeoContext = {
    visitorCountry: null,
    visitorCurrency: TCurrency.USD,
    exchangeRates: {},
    isLoading: true,
}

const GeoContext = createContext<IGeoContext>(defaultGeoContext)

interface ICacheEntry<T> {
    data: T
    timestamp: number
}

function getCachedData<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key)
        if (!raw) {
            return null
        }
        const entry: ICacheEntry<T> = JSON.parse(raw)
        if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
            localStorage.removeItem(key)
            return null
        }
        return entry.data
    } catch {
        return null
    }
}

function setCachedData<T>(key: string, data: T): void {
    try {
        const entry: ICacheEntry<T> = { data, timestamp: Date.now() }
        localStorage.setItem(key, JSON.stringify(entry))
    } catch {
        // localStorage may be full or unavailable
    }
}

export function GeoProvider({ children }: { children: ReactNode }) {
    const [geo, setGeo] = useState<IGeoContext>(() => {
        // Try to load from cache immediately for no-flash
        const cachedGeo = getCachedData<{ country: string | null; currency: string }>(GEO_CACHE_KEY)
        const cachedRates = getCachedData<Record<string, number>>(RATES_CACHE_KEY)

        if (cachedGeo) {
            return {
                visitorCountry: cachedGeo.country,
                visitorCurrency: (cachedGeo.currency as TCurrency) || TCurrency.USD,
                exchangeRates: cachedRates || {},
                isLoading: !cachedRates, // Still loading if rates aren't cached
            }
        }
        return defaultGeoContext
    })

    useEffect(() => {
        let cancelled = false

        const fetchGeoData = async () => {
            // Fetch geo info
            const cachedGeo = getCachedData<{ country: string | null; currency: string }>(
                GEO_CACHE_KEY,
            )
            let country: string | null = cachedGeo?.country ?? null
            let currency: TCurrency = (cachedGeo?.currency as TCurrency) ?? TCurrency.USD

            if (!cachedGeo) {
                try {
                    const geoRes = await fetch('/api/geo')
                    if (geoRes.ok) {
                        const geoData = (await geoRes.json()) as {
                            country: string | null
                            currency: string
                        }
                        country = geoData.country
                        currency = getCurrencyForCountry(country)
                        setCachedData(GEO_CACHE_KEY, { country, currency })
                    }
                } catch {
                    // Geo detection failed, use defaults
                }
            }

            // Fetch exchange rates
            let rates: Record<string, number> =
                getCachedData<Record<string, number>>(RATES_CACHE_KEY) || {}

            if (Object.keys(rates).length === 0) {
                try {
                    const ratesRes = await fetch('/api/exchange-rates')
                    if (ratesRes.ok) {
                        const ratesData = (await ratesRes.json()) as {
                            rates: Record<string, number>
                        }
                        rates = ratesData.rates
                        setCachedData(RATES_CACHE_KEY, rates)
                    }
                } catch {
                    // Rates fetch failed, use empty
                }
            }

            if (!cancelled) {
                setGeo({
                    visitorCountry: country,
                    visitorCurrency: currency,
                    exchangeRates: rates,
                    isLoading: false,
                })
            }
        }

        fetchGeoData().catch(() => {
            // Errors handled inside fetchGeoData
        })

        return () => {
            cancelled = true
        }
    }, [])

    return <GeoContext.Provider value={geo}>{children}</GeoContext.Provider>
}

export function useGeoContext(): IGeoContext {
    return useContext(GeoContext)
}
